import { useState } from "react";
import { Tooltip } from "@mui/material";
import {
  SectionFlexColumn,
  SectionFlexRow,
} from "../../../../components/General";
import T from "../../../../components/Typography";
import useGetExternalAccounts from "../../../../hooks/queries/employee/useGetExternalAccounts";
import { colors } from "../../../../styles";
import { AchTransferItem, ExternalAccount } from "../../../../types";
import useGetAchTransfers from "../../../../hooks/queries/employee/useGetAchTransfers";
import useGetAccount from "../../../../hooks/queries/employee/useGetAccount";
import { ReactComponent as ArrowRight } from "../../../../assets/arrowright.svg";
import { ReactComponent as CopyIcon } from "../../../../assets/icons/copy.svg";
import { ReactComponent as BankIcon } from "../../../../assets/icons/adminDashboard/bank.svg";
import PlaidConnection from "../../../../components/PlaidConnection";
import { BASE64_IMAGE } from "../../../../helpers/image";
import { useStoreActions, useStoreState } from "../../../../store";
import { formatDate } from "../../../../helpers/dates";
import { formatCurrency } from "../../../../helpers/currency";
import PageContent from "../../../../components/Layout/PageContent";
import { useIsMobile } from "../../../../helpers/ui";
import { showDebitCardFeature } from "../../../../helpers/debitcard";

export const TransfersPageContent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isMobile = useIsMobile();
  return (
    <PageContent
      customStyles={
        !isMobile ? { display: "flex", justifyContent: "center" } : undefined
      }
    >
      <SectionFlexColumn
        sx={
          !isMobile
            ? {
                width: "752px",
                maxWidth: "752px",
                minHeight: "342px",
              }
            : undefined
        }
      >
        {children}
      </SectionFlexColumn>
    </PageContent>
  );
};

const ExternalAccountItem = ({
  account,
  allowSelect,
}: {
  account: ExternalAccount;
  allowSelect: boolean;
}) => {
  const transferState = useStoreState((state) => state.transfer);
  const transferActions = useStoreActions((state) => state.transfer);
  const handleSelect = () => {
    if (allowSelect) {
      transferActions.setExternalAccount(account);
    }
  };
  return (
    <SectionFlexRow
      sx={{
        borderBottom: `1px solid ${colors.gray_200}`,
        paddingTop: "8px",
        paddingBottom: "8px",
        backgroundColor:
          transferState.externalAccount?.id === account.id
            ? colors.teal_50
            : undefined,
        ...(allowSelect && {
          cursor: "pointer",
          "&:hover": {
            backgroundColor: colors.teal_50,
          },
        }),
      }}
      onClick={handleSelect}
    >
      <div style={{ marginRight: "12px" }}>
        {account.plaid_institution_logo ? (
          <img
            src={`${BASE64_IMAGE}${account.plaid_institution_logo}`}
            alt="bank logo"
            style={{
              width: "34px",
              height: "34px",
              border: `1px solid ${colors.gray_400}`,
              padding: "2px",
              borderRadius: "20px",
            }}
          />
        ) : (
          <BankIcon
            style={{
              width: "34px",
              height: "34px",
              border: `1px solid ${colors.gray_400}`,
              padding: "2px",
              borderRadius: "20px",
            }}
          />
        )}
      </div>
      <SectionFlexColumn>
        <T variant="mdMed">{account.nickname}</T>
        <T variant="mdMed" color={colors.gray_400}>
          {account.plaid_institution_name}
        </T>
      </SectionFlexColumn>
    </SectionFlexRow>
  );
};

export const YourAccounts = ({
  showTitle = true,
  buttonWidth,
  allowSelect = false,
}: {
  showTitle?: boolean;
  buttonWidth?: string;
  allowSelect?: boolean;
}) => {
  const { data, status } = useGetExternalAccounts();

  if (status === "loading") {
    return <T variant="mdReg">Loading external accounts...</T>;
  }
  if (status === "error") {
    return <T variant="mdReg">Error loading external accounts</T>;
  }

  const renderAccounts = (allowSelect: boolean) => {
    return data.results.map((account) => (
      <ExternalAccountItem
        key={account.id}
        account={account}
        allowSelect={allowSelect}
      />
    ));
  };

  const hasAccounts = data.results.length > 0;
  return (
    <SectionFlexColumn>
      {showTitle && (
        <T variant="headingSm" marginBottom={"12px"}>
          Your Accounts
        </T>
      )}
      <div>
        {hasAccounts ? (
          renderAccounts(allowSelect)
        ) : (
          <T variant="mdReg" color={colors.gray_600}>
            You haven't connected any external accounts yet. Connecting one
            makes it easy to move money.
          </T>
        )}
        <PlaidConnection buttonWidth={buttonWidth} />
      </div>
    </SectionFlexColumn>
  );
};

const TransferItem = ({ transfer }: { transfer: AchTransferItem }) => {
  const getStatus = () => {
    let text = "";
    let color = "";
    if (transfer.simple_status === "COMPLETE") {
      text = "Complete";
      color = colors.green;
    }
    if (transfer.simple_status === "PENDING") {
      text = "Pending";
      color = colors.yellow;
    }
    if (transfer.simple_status === "ERROR") {
      text = "Error";
      color = colors.red;
    }
    return { text, color };
  };
  return (
    <SectionFlexRow
      sx={{
        justifyContent: "space-between",
        borderBottom: `1px solid ${colors.gray_200}`,
        padding: "12px 0",
      }}
    >
      <SectionFlexColumn>
        <T variant="mdMed">{formatCurrency(transfer.amount, 2)}</T>
        <T variant="mdReg" color={colors.gray_400}>
          {transfer.direction === "add_money" ? "Added Money" : "Sent Money"}
        </T>
      </SectionFlexColumn>
      <SectionFlexColumn>
        <T variant="mdReg" color={getStatus().color}>
          {getStatus().text}
        </T>
        <T variant="mdReg" color={colors.gray_400}>
          {formatDate(transfer.timestamp, "MM/DD/YYYY")}
        </T>
      </SectionFlexColumn>
    </SectionFlexRow>
  );
};

export const YourRecentTransfers = () => {
  const { data, status } = useGetAchTransfers();

  if (status === "loading") {
    return <T variant="mdReg">Loading recent transfers...</T>;
  }

  if (status === "error") {
    return <T variant="mdReg">Error loading recent transfers</T>;
  }

  const renderTransfers = () => {
    return data.map((transfer) => (
      <TransferItem key={transfer.id} transfer={transfer} />
    ));
  };

  const hasTransfers = data?.length > 0;
  return (
    <SectionFlexColumn sx={{ marginTop: "32px" }}>
      <T variant="headingSm" marginBottom={"12px"}>
        Your Recent Transfers
      </T>
      <div>
        {hasTransfers ? (
          renderTransfers()
        ) : (
          <T variant="mdReg" color={colors.gray_600}>
            No transfers yet. Connect an external account to make a transfer.
          </T>
        )}
      </div>
    </SectionFlexColumn>
  );
};

export const AccountNumbers = ({
  accountNum,
  routingNum,
}: {
  accountNum: string;
  routingNum: string;
}) => {
  const [toolTipText, setToolTipText] = useState("Click to copy");
  const handleCopy = async (text: string) => {
    if ("clipboard" in navigator) {
      await navigator.clipboard.writeText(text);
    } else {
      document.execCommand("copy", true, text);
    }
    setToolTipText("Copied!");
    setInterval(() => {
      setToolTipText("Click to copy");
    }, 3000);
    return;
  };
  return (
    <SectionFlexColumn sx={{ gap: "16px" }}>
      <SectionFlexRow sx={{ justifyContent: "space-between" }}>
        <SectionFlexColumn>
          <T variant="mdMed">Account Number</T>
          <T variant="mdMed" color={colors.gray_600}>
            {accountNum}
          </T>
        </SectionFlexColumn>
        <Tooltip
          title={toolTipText}
          placement="top"
          style={{ cursor: "pointer" }}
        >
          <CopyIcon onClick={() => handleCopy(accountNum)} />
        </Tooltip>
      </SectionFlexRow>
      <SectionFlexRow sx={{ justifyContent: "space-between" }}>
        <SectionFlexColumn>
          <T variant="mdMed">Routing Number</T>
          <T variant="mdMed" color={colors.gray_600}>
            {routingNum}
          </T>
        </SectionFlexColumn>
        <Tooltip
          title={toolTipText}
          placement="top"
          style={{ cursor: "pointer" }}
        >
          <CopyIcon onClick={() => handleCopy(routingNum)} />
        </Tooltip>
      </SectionFlexRow>
    </SectionFlexColumn>
  );
};

export const OtherOptions = () => {
  const { data, status } = useGetAccount();

  if (status === "loading") {
    return null;
  }

  if (status === "error") {
    return <T variant="mdReg">Error loading account information</T>;
  }

  return (
    <SectionFlexColumn sx={{ marginTop: "32px" }}>
      <T variant="headingSm" marginBottom={"12px"}>
        Other Options
      </T>
      {showDebitCardFeature(data.challenger_saving.debit_card) && (
        <a
          href="https://allpoint.locatorsearch.net"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: colors.black,
          }}
        >
          <div
            style={{
              borderBottom: `1px solid ${colors.gray_200}`,
              paddingBottom: 12,
            }}
            className="flex space-between"
          >
            <SectionFlexColumn>
              <T variant="lgMed">Find ATMs</T>
              <T variant="mdReg" color={colors.gray_600}>
                Use one of our 55,000 fee-free ATMs
              </T>
            </SectionFlexColumn>
            <ArrowRight />
          </div>
        </a>
      )}
      <SectionFlexColumn sx={{ marginTop: "12px", marginBottom: "16px" }}>
        <T variant="lgMed">Direct Deposit</T>
        <T variant="mdReg" color={colors.gray_600}>
          You can set up direct deposit online through your employer by sharing
          your TIFIN @Work Account details below:
        </T>
      </SectionFlexColumn>
      <AccountNumbers
        accountNum={data.challenger_saving.account_number}
        routingNum={data.challenger_saving.routing_number}
      />
    </SectionFlexColumn>
  );
};
